import * as React from "react";
import { useState } from "react";
import Tabs from "@mui/material/Tabs";
import TabContext from "@mui/lab/TabContext";
import Tab from "@mui/material/Tab";
import TabPanel from "@mui/lab/TabPanel";
import TabList from "@mui/lab/TabList";
import styles from "./tabs.module.scss";
import { useLocation } from "react-router-dom";
import { UserMenuHeader } from "../userProfileHeader/userProfile";
import Box from "@mui/material/Box";
import { EvesSidebarToggle } from "../../atoms/sidebarToggle/sidebarToggle";      

interface LinkTabProps {
  label?: string;
  href?: string;
  icon?: any;
  component?: any;
  value?: any;
  "data-cy"?: string;
}

function EvesLinkTab(props: LinkTabProps) {
  return (
    <Tab
      component="a"
      iconPosition="start"
      className={styles.tabsItem}
      onClick={(event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
        event.preventDefault();
      }}
      {...props}
    />
  );
}

// JIRA: EVES-5483 - Typed tab config item (replaces any in the tab maps)
export interface ITabConfigItem {
  name: string;
  path?: string;
  icon?: string | React.ReactElement;
  component?: React.ReactNode;
  visible?: boolean;
  dataCy?: string;
  relatedPaths?: string[];
}

export const getSelectedTabIndex = (config: any[], path: any) => {
  // Find the index of the visible tab that matches the current path (or optional relatedPaths, e.g. O&M drill-down)
  const visibleIndex = config.findIndex((item: any) => {
    if (!item.visible) return false;
    if (item.path === path) return true;
    if (Array.isArray(item.relatedPaths) && item.relatedPaths.includes(path)) return true;
    return false;
  });
  return visibleIndex === -1 ? 0 : visibleIndex;
};

export default function EvesTabs(props: any) {
  const config = props.config;
  const tabConfigData: ITabConfigItem[] = React.useMemo(() => {
    return config.filter((item: ITabConfigItem) => item.visible);
  }, []);

  const location = useLocation();
  const [value, setValue] = React.useState(getSelectedTabIndex(tabConfigData, location.pathname));

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    if (!props?.containsLinks) {
      setValue(newValue);
    }
    props.onChange(event.currentTarget.getAttribute("href"));
  };

  React.useEffect(() => {
    // EVES-3259 - Tab selection based on URL
    const newIndex = getSelectedTabIndex(tabConfigData, location.pathname);
    setValue(newIndex);
  }, [location.pathname, tabConfigData]);

  const [state, setStateData] = useState({
    selectedTab: "1",
  } as any);

  const tabChange = (newValue: any) => {
    setStateData((currentData: any) => {
      return {
        ...currentData,
        selectedTab: newValue,
      };
    });
  };

  const scrollButtons = props.scrollButtons;

  return (
    <>
      {!props.isTypeComponent && (
        <div className={styles.tabs}>
          <Box sx={{ display: "flex", alignItems: "center", flex: 1, minWidth: 0, "& .MuiTabs-root": { minWidth: 0, minHeight: "auto" } }}>
            <EvesSidebarToggle />
            <Tabs value={value} onChange={handleChange} aria-label="tabs" variant="scrollable" scrollButtons={scrollButtons}>
              {tabConfigData.map((item, index) => (
                <EvesLinkTab label={item.name} href={item.path} key={index} icon={item.icon} data-cy={item.dataCy} />
              ))}
            </Tabs>
          </Box>
          <UserMenuHeader />
        </div>
      )}

      {props.isTypeComponent && (
        <TabContext value={state.selectedTab}>
         {/* JIRA: EVES-5483 - Toggle sits outside TabList so MUI does not count it in the scrollable tab width */}
         <div className={styles.tabsIcon}>
            <EvesSidebarToggle />
            <TabList
              onChange={(event: React.SyntheticEvent, newValue: string) => tabChange(newValue)}
              aria-label="tabs"
              variant="scrollable"
              scrollButtons={scrollButtons}
             sx={{ flex: 1, minWidth: 0, minHeight: "auto" }}
            >
              {tabConfigData.map((item, index) => (
                <EvesLinkTab label={item.name} value={(index + 1).toString()} key={index} icon={item.icon} />
              ))}
              {props.hasTabsActions && (
                <div className={styles.tabsActions}>
                  <props.tabsActions />
                </div>
              )}
            </TabList>
         </div>
          {tabConfigData.map((item, index) => (
            <TabPanel value={(index + 1).toString()} className={styles.tabsPanel} key={index}>
              {item.component}
            </TabPanel>
          ))}
        </TabContext>
      )}
    </>
  );
}
